import { useEffect, useState } from 'react';
import { api } from '../../../services/api';
import type { AgentDetail, SkillSummary } from '../../../types/agent-studio';

export interface SkillsTabProps {
  agent: AgentDetail;
}

const SKILL_TYPE_LABELS: Record<SkillSummary['skill_type'], string> = {
  tool_call:      'Tool Call',
  data_retrieval: 'Data Retrieval',
  computation:    'Computation',
  injection:      'Injection',
};

function SkillSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {[0, 1].map(i => (
        <div key={i} className="card" style={{ padding: '20px', opacity: 0.4, height: '80px' }} />
      ))}
    </div>
  );
}

export function SkillsTab({ agent }: SkillsTabProps) {
  const [skills, setSkills] = useState<SkillSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const activeSkillNames: string[] = agent.active_version?.skills ?? [];

  useEffect(() => {
    if (!agent.active_version || activeSkillNames.length === 0) {
      setLoading(false);
      setSkills([]);
      return;
    }

    let cancelled = false;
    async function fetchSkills() {
      try {
        setLoading(true);
        setError(null);
        const res = await api.listSkills();
        if (!cancelled) {
          const matched = res.skills.filter(s => activeSkillNames.includes(s.system_name));
          setSkills(matched);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchSkills();
    return () => { cancelled = true; };
  }, [agent.active_version?.id]);

  if (!agent.active_version || activeSkillNames.length === 0) {
    return (
      <div className="card" style={{ padding: '24px', textAlign: 'center' }}>
        <p className="t-body" style={{ color: 'var(--ink-secondary)' }}>У этой версии нет навыков.</p>
      </div>
    );
  }

  if (loading) return <SkillSkeleton />;

  if (error) {
    return (
      <div className="card" style={{ padding: '24px', borderLeft: '3px solid var(--red)' }}>
        <div className="t-eyebrow" style={{ color: 'var(--red)' }}>Ошибка загрузки навыков</div>
        <p className="t-body-sm" style={{ marginTop: '8px', color: 'var(--ink-secondary)' }}>{error}</p>
      </div>
    );
  }

  if (!skills || skills.length === 0) {
    return (
      <div className="card" style={{ padding: '24px', textAlign: 'center' }}>
        <p className="t-body" style={{ color: 'var(--ink-secondary)' }}>У этой версии нет навыков.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {skills.map(skill => (
        <div key={skill.system_name} className="card" style={{ padding: '20px', display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="t-eyebrow" style={{ marginBottom: '4px' }}>
              {SKILL_TYPE_LABELS[skill.skill_type]}
            </div>
            <div className="t-display" style={{ fontSize: '18px', marginBottom: '6px' }}>
              {skill.display_name}
            </div>
            <p className="t-body-sm" style={{ color: 'var(--ink-secondary)', margin: 0 }}>
              {skill.description ?? '—'}
            </p>
          </div>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-tertiary)', whiteSpace: 'nowrap', paddingTop: '2px' }}>
            {skill.system_name}
          </span>
        </div>
      ))}
    </div>
  );
}
